import { createContext, useContext } from 'react';
import { colors } from '../styles/theme';

/** El mismo teal claro de `MARKET_TEAL_LIGHT` (chips Alto / Bajo del modal). */
const TEAL_LIGHT = '#7CC5C4';

/**
 * Paleta del informe. `dark` es la del modal (no cambiar los valores: la vista en pantalla
 * depende de que coincidan). `paper` es la hoja de «Descargar o imprimir» (fondo blanco).
 * Los hex de `paper` son los del informe de anuncios en tono papel.
 */
const CHIP_BASE = {
  en_linea: { color: '#FFFFFF', bgcolor: colors.primary, border: `1px solid ${colors.primary}` },
  alto: { pl: 0.5, bgcolor: 'transparent' },
  bajo: { pl: 0.5, bgcolor: 'transparent' },
};

export const MARKET_TONE_DARK = {
  paper: false,
  card: colors.backgroundSurface,
  border: '#333',
  head: colors.textPanelTitle,
  big: '#FFFFFF',
  text: 'rgba(255,255,255,0.85)',
  muted: colors.textSecondary,
  legendRight: 'rgba(255,255,255,0.75)',
  line: '#2E2E2E',
  accent: TEAL_LIGHT,
  accentSoft: 'rgba(72,146,145,0.15)',
  suffix: 'rgba(255,255,255,0.7)',
  mine: '#FFFFFF',
  barTrack: '#2C2C2C',
  barMine: colors.primary,
  barOther: 'rgba(72,146,145,0.45)',
  barBg: '#333',
  barIqr: 'rgba(255,255,255,0.16)',
  barMedian: '#BBBBBB',
  barUnitBorder: colors.backgroundSurface,
  barUnitOnLine: '#FFFFFF',
  rowBorder: '#333',
  rowActive: 'rgba(72,146,145,0.08)',
  thumbBg: '#2A2A2A',
  thumbIcon: 'rgba(255,255,255,0.4)',
  chipStrong: TEAL_LIGHT,
  chipWeak: 'rgba(255,255,255,0.6)',
  chipStrongBorder: 'rgba(124,197,196,0.55)',
  chipWeakBorder: 'rgba(255,255,255,0.22)',
  chipFilled: 'rgba(72,146,145,0.2)',
  noticeBg: 'rgba(255,255,255,0.05)',
  noticeBorder: 'rgba(255,255,255,0.14)',
  noticeIcon: 'rgba(255,255,255,0.6)',
  noticeTitle: '#FFFFFF',
  noticeText: 'rgba(255,255,255,0.7)',
  chartGrid: '#2F2F2F',
  chartAxis: '#7F8185',
  chartGap: 'rgba(255,255,255,0.04)',
  chartLabel: '#9A9A9A',
  chartLine: '#5AA5A4',
  chip: {
    en_linea: CHIP_BASE.en_linea,
    alto: { ...CHIP_BASE.alto, color: TEAL_LIGHT, border: '1px solid rgba(124,197,196,0.55)' },
    bajo: { ...CHIP_BASE.bajo, color: TEAL_LIGHT, border: '1px solid rgba(124,197,196,0.55)' },
    neutral: {
      color: 'rgba(255,255,255,0.6)',
      bgcolor: 'rgba(255,255,255,0.06)',
      border: '1px solid rgba(255,255,255,0.18)',
    },
  },
};

export const MARKET_TONE_PAPER = {
  paper: true,
  card: '#FFFFFF',
  border: '#dfe4e8',
  head: '#111111',
  big: '#111111',
  text: '#1d2327',
  muted: '#5f6b73',
  legendRight: '#5f6b73',
  line: '#e8ecee',
  accent: '#2f7f79',
  accentSoft: 'rgba(47,127,121,0.12)',
  suffix: '#5f6b73',
  mine: '#111111',
  barTrack: '#e8ecee',
  barMine: colors.primary,
  barOther: 'rgba(72,146,145,0.55)',
  barBg: '#e8ecee',
  barIqr: 'rgba(29,35,39,0.12)',
  barMedian: '#5f6b73',
  barUnitBorder: '#FFFFFF',
  barUnitOnLine: colors.primary,
  rowBorder: '#dfe4e8',
  rowActive: 'rgba(72,146,145,0.08)',
  thumbBg: '#f3f5f6',
  thumbIcon: '#5f6b73',
  chipStrong: '#2f7f79',
  chipWeak: '#5f6b73',
  chipStrongBorder: 'rgba(47,127,121,0.55)',
  chipWeakBorder: '#dfe4e8',
  chipFilled: 'rgba(72,146,145,0.12)',
  noticeBg: '#f3f5f6',
  noticeBorder: '#dfe4e8',
  noticeIcon: '#5f6b73',
  noticeTitle: '#111111',
  noticeText: '#5f6b73',
  chartGrid: '#e8ecee',
  chartAxis: '#5f6b73',
  chartGap: 'rgba(29,35,39,0.04)',
  chartLabel: '#5f6b73',
  chartLine: colors.primary,
  chip: {
    en_linea: CHIP_BASE.en_linea,
    alto: { ...CHIP_BASE.alto, color: colors.primary, border: `1px solid ${colors.primary}` },
    bajo: { ...CHIP_BASE.bajo, color: colors.primary, border: `1px solid ${colors.primary}` },
    neutral: { color: '#5f6b73', bgcolor: '#f3f5f6', border: '1px solid #dfe4e8' },
  },
};

const MarketToneContext = createContext(MARKET_TONE_DARK);

export function MarketToneProvider({ tone = 'dark', children }) {
  const value = tone === 'paper' ? MARKET_TONE_PAPER : MARKET_TONE_DARK;
  return <MarketToneContext.Provider value={value}>{children}</MarketToneContext.Provider>;
}

export function useMarketTone() {
  return useContext(MarketToneContext);
}
